import {List} from 'immutable';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  addGenericErrorToast,
  Badge,
  Icon,
  LoadingSpinner,
  Menu,
  SearchInput,
  Text,
  WithTooltip
} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import {history} from 'client/history';
import ClassroomFlair from 'client/StudentHomepage/ClassroomFlair';

import {EDGE_MENU_POPPER_PROPS} from '../config/menuPopperProps';
import {
  getTeacherSidemenuClassroomsQuery,
  useTeacherClassrooms
} from '../hooks/useTeacherClassrooms';
import {useTeacherDashboardStore} from '../useTeacherDashboardStore';

import type {NavScope} from './teacherNavScope';
import {
  getNavScope,
  getNavTab,
  isEmptyActiveClass,
  isScopeNeutralRoute,
  resolveScopeRoute
} from './teacherNavScope';
import {TOOLTIP_PROPS} from './config';

const SEARCH_DEBOUNCE_MS = 250;

const MENU_PANEL_CLASS =
  '[&_.o-menu-content]:w-fit! [&_.o-menu-content]:max-w-[304px]! [&_.o-menu-content]:max-h-[485px]! [&_.o-menu-content]:overflow-y-auto!';

const ClassRow = ({
  classroom,
  leading,
  scope,
  onSelect
}: {
  classroom: ClassroomModelV1;
  leading: React.ReactNode;
  scope: NavScope;
  onSelect: (classId: string) => void;
}) => (
  <Menu.Item
    // Menu.Item renders `selected` as a CSS class only — announce it to AT too.
    aria-current={(scope.kind === 'class' && scope.classId === classroom.getId()) || undefined}
    className='px-x2! py-x1-half!'
    selected={scope.kind === 'class' && scope.classId === classroom.getId()}
    onClick={() => onSelect(classroom.getId())}
  >
    <span className='flex w-full min-w-0 items-center'>
      {leading}
      <span className='min-w-0 truncate'>{classroom.getName()}</span>
    </span>
  </Menu.Item>
);

const AllClassesBadge = () => (
  <Badge
    aria-hidden='true'
    className='mr-x1 background-brand-subtle border-none [&>.a-text]:leading-none'
    roundness='circle'
    size='small'
  >
    <Icon className='text-[15px]' color='brand' icon='chalkboard' iconStyle='regular' />
  </Badge>
);

interface Props {
  collapsed?: boolean;
  pathname: string;
  /** Effective nav scope from the parent (Home keeps the remembered class); falls back to the route. */
  scope?: NavScope;
}

/**
 * Sidemenu scope switcher: search + pick "All Classes" or a class (active/archived).
 * Search hits the classrooms endpoint per status (separate requests, archived lazy);
 * results stay local so the collapsed rail's full list is never affected.
 */
export const ClassSelector = ({collapsed = false, pathname, scope: scopeProp}: Props) => {
  const scope = scopeProp ?? getNavScope(pathname);
  const currentTab = getNavTab(pathname);

  const {archivedClassrooms, areArchivedClassroomsLoading, classrooms, fetchArchivedClassrooms} =
    useTeacherClassrooms();
  const isShowingArchived = useTeacherDashboardStore((s) => s.isShowingArchivedClassrooms);
  const setIsShowingArchived = useTeacherDashboardStore((s) => s.setIsShowingArchivedClassrooms);
  const setSelectedClassId = useTeacherDashboardStore((s) => s.setSelectedClassId);

  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);

  // Local, name-filtered results — never written to the store (which feeds the rail's full list).
  const [searchActive, setSearchActive] = useState<List<ClassroomModelV1> | null>(null);
  const [searchArchived, setSearchArchived] = useState<List<ClassroomModelV1> | null>(null);
  const activeGenRef = useRef(0);
  const archivedGenRef = useRef(0);

  // One race-guarded fetch per status; runs on the search event, not an effect.
  const runSearch = useCallback(
    (
      status: 'active' | 'archived',
      genRef: React.MutableRefObject<number>,
      setResults: (results: List<ClassroomModelV1>) => void,
      term: string
    ) => {
      const generation = (genRef.current += 1);
      const query = getTeacherSidemenuClassroomsQuery(status, term);
      // A failed term is cached as an empty stub — re-dirty so retyping it retries instead of
      // silently showing "No classes found".
      if (!query.isResourceValid()) {
        query.invalidateInterest();
      }
      query
        .getResourcePromise()
        .then((res) => {
          if (generation === genRef.current) setResults(res as List<ClassroomModelV1>);
        })
        .catch(() => {
          if (generation === genRef.current) {
            setResults(List());
            addGenericErrorToast();
          }
        });
    },
    []
  );

  const debouncedSearch = useMemo(
    () =>
      // oxlint-disable-next-line react/react-compiler -- the ref is only read when the debounced/stable callback fires later, not during render
      debounce((term: string) => {
        setSearching(Boolean(term));
        if (!term) {
          // Bump generations so a slower in-flight response can't repopulate after clearing.
          activeGenRef.current += 1;
          archivedGenRef.current += 1;
          setSearchActive(null);
          setSearchArchived(null);
          return;
        }
        // Clear both lists so the dropdown shows a loader until the new results arrive.
        setSearchActive(null);
        setSearchArchived(null);
        runSearch('active', activeGenRef, setSearchActive, term);
        // Search archived too so we know whether to show the toggle (rows stay collapsed until expanded).
        runSearch('archived', archivedGenRef, setSearchArchived, term);
      }, SEARCH_DEBOUNCE_MS),
    [runSearch]
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const activeList = searching ? (searchActive ?? List<ClassroomModelV1>()) : classrooms;
  const archivedList = searching
    ? (searchArchived ?? List<ClassroomModelV1>())
    : archivedClassrooms;
  const searchLoading = searching && (searchActive === null || searchArchived === null);

  // Look in both lists so an archived class still resolves its name/flair in the trigger.
  const activeClassroom =
    scope.kind === 'class'
      ? (classrooms.find((c) => c.getId() === scope.classId) ??
        archivedClassrooms.find((c) => c.getId() === scope.classId))
      : undefined;
  const label = scope.kind === 'all' ? 'All Classes' : (activeClassroom?.getName() ?? 'Class');

  const resetSearch = () => {
    debouncedSearch.cancel();
    activeGenRef.current += 1;
    archivedGenRef.current += 1;
    setSearchTerm('');
    setSearching(false);
    setSearchActive(null);
    setSearchArchived(null);
  };

  const goToScope = (target: NavScope, classroom?: ClassroomModelV1) => {
    resetSearch();
    if (target.kind === 'all') {
      // Clearing the remembered class flips the derived scope to "All Classes". On a scope-neutral
      // route (Dashboard, Folders) that alone updates the selector in place, so stay put instead of
      // bouncing to /assignments — the route has no per-scope equivalent to navigate to.
      setSelectedClassId(null);
      if (isScopeNeutralRoute(pathname)) {
        return;
      }
    }
    history.pushState(
      null,
      resolveScopeRoute(target, currentTab, {emptyActiveClass: isEmptyActiveClass(classroom)})
    );
  };

  const handleToggleArchived = () => {
    const willShow = !isShowingArchived;
    setIsShowingArchived(willShow);
    // Non-search expand: lazy-load the store's archived list once (search results are fetched on type).
    if (willShow && !searching && archivedClassrooms.isEmpty() && !areArchivedClassroomsLoading) {
      fetchArchivedClassrooms();
    }
  };

  const leadingBadge =
    scope.kind === 'class' && activeClassroom ? (
      <ClassroomFlair classroom={activeClassroom} isForAll={false} />
    ) : (
      <AllClassesBadge />
    );

  return (
    <Menu
      // Badges default to cursor:default; force pointer so hovering a class/All-Classes icon matches its clickable row.
      className='w-full [&_.a-badge]:cursor-pointer!'
      closeOnItemClick
      popperProps={EDGE_MENU_POPPER_PROPS}
      position='bottom-start'
      positionFixed
    >
      <Menu.Trigger fillWrapper>
        {(triggerProps) =>
          collapsed ? (
            // Card trigger as a div (atomic Button's variant styling would fight the card); Menu wires interactivity via triggerProps.
            // Collapsed rail has no visible label, so surface the current selection as a tooltip.
            // WithTooltip's className styles the popup; the wrapper (which must span the rail) takes wrapperProps.
            <WithTooltip
              as='div'
              content={label}
              wrapperProps={{className: 'w-full'}}
              {...TOOLTIP_PROPS}
            >
              <div
                {...triggerProps}
                aria-label='Switch class'
                className='rounded-x2 border-primary background-primary px-x1 py-x1-quarter elevation-shadow-l1 *:mr-x0! hover:background-subtle-hover flex w-full cursor-pointer items-center justify-center border'
                role='button'
              >
                {leadingBadge}
              </div>
            </WithTooltip>
          ) : (
            <div
              {...triggerProps}
              className='gap-x1-quarter rounded-x2 border-primary background-primary px-x2 py-x1-quarter elevation-shadow-l1 hover:background-subtle-hover flex w-full cursor-pointer items-center border text-left'
              role='button'
            >
              <span className='shrink-0'>{leadingBadge}</span>
              <span className='flex min-w-0 grow flex-col overflow-hidden'>
                <Text className='block w-full truncate' color='primary' size='m'>
                  {label}
                </Text>
                <Text className='text-xs!' color='tertiary'>
                  Switch class
                </Text>
              </span>
              <Icon className='content-primary shrink-0' icon='caret-down' />
            </div>
          )
        }
      </Menu.Trigger>
      <Menu.Content className={MENU_PANEL_CLASS}>
        <div
          className='pb-x1'
          onKeyDown={(e) => {
            // Contain Enter/Left/Right in the input, but ArrowUp/Down must reach MenuContent's
            // roving focus — else keyboard users are trapped in search with no path to the rows.
            if (e.key === 'Enter' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.stopPropagation();
            }
          }}
        >
          <SearchInput
            aria-label='Search classes'
            autoFocus
            className='w-full'
            inputClassName='truncate'
            placeholder='Search'
            size='s'
            value={searchTerm}
            onChange={(_e, value) => {
              setSearchTerm(value);
              debouncedSearch(value);
            }}
            onReset={resetSearch}
          />
        </div>
        <Menu.Item
          aria-current={scope.kind === 'all' || undefined}
          className='px-x2! py-x1-half!'
          selected={scope.kind === 'all'}
          onClick={() => goToScope({kind: 'all'})}
        >
          <span className='flex items-center'>
            <AllClassesBadge />
            All Classes
          </span>
        </Menu.Item>
        <Menu.Divider className='py-x1-half!' />
        {searchLoading ? (
          <div className='py-x4 flex justify-center'>
            <LoadingSpinner bottomMarginLevel={0} size={2} />
          </div>
        ) : (
          <>
            {activeList
              .map((classroom) => (
                <ClassRow
                  classroom={classroom}
                  key={classroom.getId()}
                  leading={<ClassroomFlair classroom={classroom} isForAll={false} />}
                  onSelect={(classId) => goToScope({kind: 'class', classId}, classroom)}
                  scope={scope}
                />
              ))
              .toArray()}
            {searching && activeList.isEmpty() && archivedList.isEmpty() && (
              <div className='px-x2 py-x2 text-center'>
                <Text color='secondary' size='s'>
                  No classes found
                </Text>
              </div>
            )}
          </>
        )}
        {/* Hide the toggle when a search has no archived matches */}
        {(!searching || !archivedList.isEmpty()) && (
          <div
            aria-expanded={isShowingArchived}
            className='group gap-x1 rounded-x1 px-x2 py-x1-half content-primary hover:background-subtle-hover flex w-full cursor-pointer items-center text-left'
            onClick={handleToggleArchived}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleArchived();
              }
            }}
            role='menuitem'
            tabIndex={0}
          >
            {isShowingArchived ? (
              <Icon fixedWidth icon='chevron-down' iconStyle='regular' />
            ) : (
              <>
                {/* archive by default; swaps to chevron-right on hover (DQA) */}
                <Icon
                  fixedWidth
                  className='group-hover:hidden'
                  icon='archive'
                  iconStyle='regular'
                />
                <Icon
                  fixedWidth
                  className='hidden group-hover:inline-block'
                  icon='chevron-right'
                  iconStyle='regular'
                />
              </>
            )}
            <Text color='inherit' size='m'>
              {isShowingArchived ? 'Hide Archived' : 'Show Archived'}
            </Text>
          </div>
        )}
        {/* Expanded archived section: rows, or (outside search, where the toggle always shows) a
            spinner while the lazy fetch resolves and an explicit no-archived-classes empty state. */}
        {isShowingArchived &&
          (archivedList.isEmpty()
            ? !searching &&
              (areArchivedClassroomsLoading ? (
                <div className='py-x2 flex justify-center'>
                  <LoadingSpinner bottomMarginLevel={0} size={2} />
                </div>
              ) : (
                <div className='px-x2 py-x2 text-center'>
                  <Text color='secondary' size='s'>
                    No archived classes
                  </Text>
                </div>
              ))
            : archivedList
                .map((classroom) => (
                  <ClassRow
                    classroom={classroom}
                    key={classroom.getId()}
                    leading={
                      <Badge
                        aria-hidden='true'
                        className='mr-x1 content-secondary border-none bg-transparent! [&>.a-text]:leading-none'
                        roundness='circle'
                        size='small'
                      >
                        {classroom.getName()?.charAt(0).toUpperCase() ?? ''}
                      </Badge>
                    }
                    onSelect={(classId) => goToScope({kind: 'class', classId}, classroom)}
                    scope={scope}
                  />
                ))
                .toArray())}
      </Menu.Content>
    </Menu>
  );
};
